/**
 * Home: the hub across Chat, Mail, Calendar and Docs (Slack's Home plus a Gmail / Google Calendar "today" digest). A
 * greeting and today's date, then Unreads, Today, Email and recent Docs, then channels, direct messages and agents at
 * work. What each section shows, its order and its limits come from core logic/home; Today and Email are
 * HomeViewModel's digests, loaded after the page renders (Chat never waits) and hidden when the workspace has no mail.
 */
import type { AgentState, Attendee, MailTabDef } from "@bundle/contracts";
import { Home, HomeViewModel, Shell, type MessageKey, type SidebarRow } from "@bundle/core";
import type { HomeEvent, HomeThread } from "@bundle/core/logic/home";
import * as Cal from "@bundle/core/logic/calendar";
import { displayName, mailAvatarPath, mailTabLabel } from "@bundle/core/logic/mail";
import {
  Avatar,
  Badge,
  Button,
  Chip,
  ConnectivityBanner,
  Icon,
  PageListRow,
  Pressable,
  ScrollView,
  Skeleton,
  Stack,
  Text,
  mailShortDate,
  onMailEvent,
  MailCodeChip,
  useFormat,
  useLocale,
  useMail,
  useNavigation,
  usePageTreeViewModel,
  usePageTree,
  useSidebar,
  useT,
  useViewModel,
  useWorkspace,
  AnimatedList,
} from "@bundle/ui";
import type { IconName } from "@bundle/tokens";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";

const STATE_KEYS: Record<AgentState, MessageKey> = {
  idle: "agent.state.idle",
  working: "agent.state.working",
  waiting: "agent.state.waiting",
  error: "agent.state.error",
  offline: "agent.state.offline",
};

const GREETING: Record<ReturnType<typeof Home.greetingPart>, MessageKey> = {
  morning: "home.greetingMorning",
  afternoon: "home.greetingAfternoon",
  evening: "home.greetingEvening",
};

function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

function subtitleOf(row: SidebarRow, t: ReturnType<typeof useT>): string | undefined {
  if (row.type === "agent") return row.activity ?? t(STATE_KEYS[row.state]);
  if (row.type === "dm" && row.organization !== undefined) return t("guest.badgeOrg", { org: row.organization });
  return undefined;
}

function Row({ row, onPress }: { row: SidebarRow; onPress: () => void }) {
  const t = useT();
  const strong = row.unread || row.mentions > 0;
  const subtitle = subtitleOf(row, t);
  return (
    <Pressable direction="horizontal" radius="lg" onPress={onPress} testID={`home-row-${row.id}`}>
      <Stack grow direction="horizontal" align="center" gap="3" paddingX="3" paddingY="2.5">
        {row.type === "channel" ? (
          <Stack padding="2" radius="md" background="bg.surface">
            <Icon name={row.isPrivate ? "lock" : "hash"} size="icon.sm" color="text.secondary" />
          </Stack>
        ) : row.type === "dm" ? (
          <Avatar name={row.avatarName} src={row.avatarUrl} presence={row.presence} square={row.isGroup || row.isAgent} size="avatar.md" />
        ) : (
          <Avatar name={row.name} src={row.avatarUrl} presence={row.presence} square size="avatar.md" />
        )}
        <Stack grow gap="0.5">
          <Text weight={strong ? "semibold" : "medium"} lines={1}>
            {row.name}
          </Text>
          {subtitle === undefined ? null : (
            <Text variant="footnote" color={row.type === "agent" ? "agent.fg" : "text.secondary"} lines={1}>
              {subtitle}
            </Text>
          )}
        </Stack>
        {row.mentions > 0 ? <Badge count={row.mentions} /> : row.unread ? <Badge dot tone="accent" /> : null}
        <Icon name="chevronRight" size="icon.sm" color="text.tertiary" />
      </Stack>
    </Pressable>
  );
}

/** A section: a small label (with an optional action on the right) over a bordered card. */
function Section({ title, icon, action, testID, children }: { title: string; icon: IconName; action?: ReactNode; testID: string; children: ReactNode }) {
  return (
    <Stack gap="1.5" testID={testID}>
      <Stack direction="horizontal" align="center" gap="2" paddingX="3">
        <Icon name={icon} size="icon.sm" color="text.tertiary" />
        <Stack grow>
          <Text variant="footnote" weight="semibold" color="text.tertiary">
            {title}
          </Text>
        </Stack>
        {action}
      </Stack>
      <Stack radius="lg" border="border.subtle" background="bg.canvas" padding="1">
        {children}
      </Stack>
    </Stack>
  );
}

function RowsSection({ title, icon, rows, onOpen, testID }: { title: string; icon: IconName; rows: SidebarRow[]; onOpen: (r: SidebarRow) => void; testID: string }) {
  return (
    <Section title={title} icon={icon} testID={testID}>
      {rows.map((r) => (
        <Row key={r.id} row={r} onPress={() => onOpen(r)} />
      ))}
    </Section>
  );
}

function Faces({ faces, more }: { faces: Attendee[]; more: number }) {
  const t = useT();
  const ws = useWorkspace();
  const members = ws.state.cache?.members ?? {};
  if (faces.length === 0) return null;
  return (
    <Stack direction="horizontal" align="center" gap="0.5">
      {faces.map((a, i) => {
        const m = a.userId === undefined ? undefined : members[a.userId];
        const name = m?.displayName ?? a.name ?? a.email ?? "?";
        return <Avatar key={a.userId ?? a.email ?? i} name={name} src={m?.avatarUrl} size="avatar.xs" />;
      })}
      {more > 0 ? (
        <Text variant="caption" color="text.tertiary">
          {t("home.moreFaces", { count: more })}
        </Text>
      ) : null}
    </Stack>
  );
}

function EventRow({ e, onOpen, onJoin }: { e: HomeEvent; onOpen: () => void; onJoin: () => void }) {
  const t = useT();
  const fmt = useFormat();
  return (
    <Stack direction="horizontal" align="center" gap="2">
      <Stack grow>
      <Pressable direction="horizontal" radius="lg" onPress={onOpen} testID={`home-event-${e.key}`}>
        <Stack grow direction="horizontal" align="center" gap="3" paddingX="3" paddingY="2.5">
          <Stack padding="2" radius="md" background={e.live ? "accent.subtle" : "bg.surface"}>
            <Icon name={e.canJoin ? "huddle" : "calendar"} size="icon.sm" color={e.live ? "accent.fg" : "text.secondary"} />
          </Stack>
          <Stack grow gap="0.5">
            <Text weight="medium" lines={1}>
              {e.title}
            </Text>
            <Stack direction="horizontal" align="center" gap="1.5" wrap>
              {e.live ? <Chip label={t("home.now")} tone="accent" /> : null}
              <Text variant="footnote" color="text.secondary">
                {e.allDay ? t("calendar.allDay") : `${fmt.time(e.start)} – ${fmt.time(e.end)}`}
              </Text>
              <Faces faces={e.faces} more={e.moreFaces} />
            </Stack>
          </Stack>
        </Stack>
      </Pressable>
      </Stack>
      {e.canJoin ? (
        <Stack paddingX="2">
          <Button size="sm" variant={e.live ? "primary" : "secondary"} icon="huddle" label={t("home.join")} onPress={onJoin} testID={`home-event-join-${e.key}`} />
        </Stack>
      ) : null}
    </Stack>
  );
}

function DayLabel({ label }: { label: string }) {
  return (
    <Stack paddingX="3" paddingY="1.5">
      <Text variant="caption" weight="semibold" color="text.tertiary">
        {label}
      </Text>
    </Stack>
  );
}

/** A tab's name in my language, from the server's strip. */
function tabName(defs: readonly MailTabDef[], id: string, locale: string): string {
  const def = defs.find((d) => d.id === id);
  return def === undefined ? id : mailTabLabel(def, locale);
}

function ThreadRow({ th, onPress }: { th: HomeThread; onPress: () => void }) {
  const t = useT();
  const locale = useLocale();
  const ws = useWorkspace();
  return (
    <Pressable direction="horizontal" radius="lg" onPress={onPress} testID={`home-thread-${th.id}`}>
      <Stack grow direction="horizontal" align="center" gap="3" paddingX="3" paddingY="2.5">
        <Avatar name={th.sender === undefined ? th.senders : displayName(th.sender)} src={th.sender === undefined ? undefined : mailAvatarPath(ws.vm.wsId, th.sender.address)} size="avatar.md" />
        <Stack grow gap="0.5">
          <Stack direction="horizontal" align="center" gap="2">
            <Stack grow>
              <Text weight={th.unread ? "semibold" : "medium"} lines={1}>
                {th.senders}
              </Text>
            </Stack>
            {th.code !== undefined && th.codeExpiresAt !== undefined ? (
              <MailCodeChip code={th.code} expiresAt={th.codeExpiresAt} onCopy={(text) => navigator.clipboard.writeText(text)} testID={`home-code-${th.id}`} />
            ) : th.needsReply ? (
              <Chip label={t("home.needsReply")} tone="warning" />
            ) : null}
            <Text variant="caption" color={th.unread ? "text.primary" : "text.tertiary"} weight={th.unread ? "semibold" : "regular"}>
              {mailShortDate(th.lastAt, locale)}
            </Text>
          </Stack>
          <Text variant="footnote" color="text.secondary" lines={1}>
            {th.snippet === "" ? th.subject : `${th.subject} — ${th.snippet}`}
          </Text>
        </Stack>
      </Stack>
    </Pressable>
  );
}

/** Start page of a new tab: what needs attention across chat, calendar, mail and docs. */
export function HomeScreen() {
  const locale = useLocale();
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  const fmt = useFormat();
  const sidebar = useSidebar();
  const tree = usePageTree();
  const treeVm = usePageTreeViewModel();
  const mail = useMail();
  const mailOn = mail !== undefined;
  const timeZone = mail?.calendar.state.timeZone ?? deviceTimeZone();
  const create = useCallback(() => new HomeViewModel(ws.vm, mailOn, timeZone), [ws.vm, mailOn, timeZone]);
  // Kept with the workspace, not the tab: back from another workspace, the agenda and digest are there at once.
  const vm = useMemo(() => ws.vm.keep(`home:${mailOn ? "mail" : "chat"}:${timeZone}`, create), [ws.vm, mailOn, timeZone, create]);
  const s = useViewModel(vm);
  useEffect(() => {
    vm.load();
    return onMailEvent((ev) => vm.onEvent(ev));
  }, [vm]);

  const cache = ws.state.cache;
  const me = cache?.members[cache.meId];
  const chat = Home.homeChat(sidebar.state.sections);
  const docs = tree.state.recent.slice(0, Home.HOME_DOCS_MAX);
  const sections = Home.homeSections({
    unreads: chat.unreads.length, agenda: s.agenda, mail: s.mail, docs: docs.length, channels: chat.channels.length, dms: chat.dms.length, agents: chat.agents.length,
  });
  const p = Cal.localParts(s.now, s.timeZone);
  const date = fmt.day({ type: "date", year: p.year, month: p.month, day: p.day, weekday: p.weekday, showYear: false });

  const open = (r: SidebarRow) => {
    if (r.type === "agent") nav.open(Shell.formatUrl({ type: "dm", ws: ws.slug, userId: r.userId }));
    else nav.open(Shell.formatUrl({ type: "channel", ws: ws.slug, channelId: r.channelId }));
  };
  const openEvent = (e: HomeEvent) => nav.open(Shell.formatUrl({ type: "calendar", ws: ws.slug, view: "day", at: e.start, event: e.eventId }));
  const joinEvent = (e: HomeEvent) => {
    if (e.channelId !== undefined) nav.open(Shell.formatUrl({ type: "huddle", ws: ws.slug, huddleId: e.channelId }));
  };
  const newPage = async () => {
    const page = await treeVm.create({});
    if (page !== undefined) nav.open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: page.id }));
  };

  const render = (kind: Home.HomeSectionKind): ReactNode => {
    switch (kind) {
      case "unreads":
        return <RowsSection key={kind} title={t("home.unreads")} icon="inbox" rows={chat.unreads} onOpen={open} testID="home-unreads" />;
      case "agenda": {
        const agenda = s.agenda;
        if (agenda === undefined) return null;
        const today = agenda.events.filter((e) => e.day === "today");
        const tomorrow = agenda.events.filter((e) => e.day === "tomorrow");
        return (
          <Section
            key={kind}
            title={t("home.today")}
            icon="calendar"
            testID="home-agenda"
            action={<Button size="sm" variant="ghost" label={t("home.openCalendar")} onPress={() => nav.open(Shell.formatUrl({ type: "calendar", ws: ws.slug, view: "day" }))} testID="home-open-calendar" />}
          >
            {agenda.noMoreToday ? (
              <Stack paddingX="3" paddingY="2.5">
                <Text color="text.secondary" testID="home-no-more-events">
                  {t("home.noMoreEvents")}
                </Text>
              </Stack>
            ) : (
              today.map((e) => <EventRow key={e.key} e={e} onOpen={() => openEvent(e)} onJoin={() => joinEvent(e)} />)
            )}
            {tomorrow.length === 0 ? null : <DayLabel label={t("home.tomorrow")} />}
            {tomorrow.map((e) => (
              <EventRow key={e.key} e={e} onOpen={() => openEvent(e)} onJoin={() => joinEvent(e)} />
            ))}
          </Section>
        );
      }
      case "mail": {
        const m = s.mail;
        if (m === undefined) return null;
        return (
          <Section
            key={kind}
            title={t("home.email")}
            icon="mail"
            testID="home-mail"
            action={<Button size="sm" variant="ghost" label={t("home.openMail")} onPress={() => nav.open(Shell.formatUrl({ type: "mail", ws: ws.slug }))} testID="home-open-mail" />}
          >
            {m.tabs.length === 0 ? null : (
              <Stack direction="horizontal" gap="1.5" paddingX="2" paddingY="2" wrap>
                {m.tabs.map((x) => (
                  <Chip
                    key={x.tab}
                    label={`${tabName(m.tabDefs, x.tab, locale)} · ${x.unread}`}
                    onPress={() => nav.open(Shell.formatUrl({ type: "mail", ws: ws.slug, tab: x.tab }))}
                    testID={`home-mail-tab-${x.tab}`}
                  />
                ))}
              </Stack>
            )}
            <AnimatedList>
              {m.threads.map((th) => (
                <Stack key={th.id} background={s.newMailIds.includes(th.id) ? "accent.subtle" : undefined} fadeBackground radius="lg">
                  <ThreadRow th={th} onPress={() => nav.open(Shell.formatUrl({ type: "mailThread", ws: ws.slug, threadId: th.id }))} />
                </Stack>
              ))}
            </AnimatedList>
          </Section>
        );
      }
      case "docs":
        return (
          <Section
            key={kind}
            title={t("home.recentDocs")}
            icon="page"
            testID="home-recent-docs"
            action={tree.state.canCreatePages ? <Button size="sm" variant="ghost" icon="pagePlus" label={t("docs.new")} onPress={() => void newPage()} testID="home-new-page" /> : undefined}
          >
            {docs.map((pg) => (
              <PageListRow
                key={pg.id}
                title={pg.title}
                icon={pg.icon}
                kind={pg.kind}
                subtitle={t("page.edited", { time: fmt.timestamp(pg.updatedAt) })}
                onPress={() => nav.open(pg.kind === "database" ? Shell.formatUrl({ type: "db", ws: ws.slug, dbId: pg.id }) : Shell.formatUrl({ type: "page", ws: ws.slug, pageId: pg.id }))}
                testID={`home-doc-${pg.id}`}
              />
            ))}
          </Section>
        );
      case "channels":
        return <RowsSection key={kind} title={t("home.channels")} icon="hash" rows={chat.channels} onOpen={open} testID="home-channels" />;
      case "dms":
        return <RowsSection key={kind} title={t("home.directMessages")} icon="users" rows={chat.dms} onOpen={open} testID="home-dms" />;
      case "agents":
        return <RowsSection key={kind} title={t("home.agents")} icon="agent" rows={chat.agents} onOpen={open} testID="home-agents" />;
    }
  };

  return (
    <Stack grow testID="home-screen">
      {ws.state.banner === undefined ? null : <ConnectivityBanner banner={ws.state.banner} />}
      <ScrollView padding="6" maxContentWidth="content.readable">
        <Stack gap="6">
          <Stack gap="1">
            <Text variant="caption" weight="semibold" color="text.tertiary" testID="home-date">
              {date}
            </Text>
            <Text variant="title1" weight="bold">
              {t(GREETING[Home.greetingPart(p.hour)], { name: me?.displayName ?? cache?.workspace.name ?? "" })}
            </Text>
            {sidebar.state.badge === 0 && ws.state.status === "loading" ? (
              <Skeleton variant="callout" testID="home-summary-loading" />
            ) : (
              <Text variant="callout" color="text.secondary">
                {sidebar.state.badge > 0 ? t("home.unread", { count: sidebar.state.badge }) : t("home.allCaughtUp")}
              </Text>
            )}
          </Stack>
          {sections.map(render)}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
